"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _react = _interopRequireDefault(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _View = _interopRequireDefault(require("react-native-web/dist/cjs/exports/View"));
var _StyleSheet = _interopRequireDefault(require("react-native-web/dist/cjs/exports/StyleSheet"));
var _Platform = _interopRequireDefault(require("react-native-web/dist/cjs/exports/Platform"));
var _ThemeProvider = require("../ThemeProvider");
var _Spacer = _interopRequireDefault(require("../Spacer"));
var _utils = require("../utils");
var _jsxRuntime = require("react/jsx-runtime");
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
const [selectProps, selectedSystemPropTypes] = (0, _utils.selectSystemProps)([_utils.a11yProps, _utils.viewProps]);

/**
 * @typedef {import('../utils/props/spacingProps.js').SpacingIndex} SpacingIndex
 * @typedef {import('../utils/props/spacingProps.js').SpacingObject} SpacingObject
 */

/**
 * A basic divider component, horizontal by default. Color and thickness can be controlled by theme.
 * Divider is based on the flexbox model and uses `alignSelf: flex` to span its flex parent.
 *
 * ## Vertical
 *
 * Vertical dividers can be created by passing a `vertical` boolean prop.
 * Note that for a vertical divider the parent flex element **must** have flex direction set to row.
 * This is the default for web but not for React Native.
 *
 * In a flexbox row, vertical dividers will automatically size to their parent height.
 *
 * ## Space
 *
 * Use this to create **space either side of the divider**. For simple cases, pass a number referring to
 * a position on the theme's spacing scale; for example, this will put a Spacer of the smallest supported
 * size either side of the divider:
 *
 * ```jsx
 * <Divider space={1} />
 * ```
 *
 * `space` prop uses `useSpacingScale` and may accept a {@link SpacingObject} or a {@link SpacingIndex} number.
 *
 * To **reduce the length of a divider** as well as creating space between it and its neighbours, wrap it in
 * a `Box` component. For example, this will have the second-smallest theme-supported spacing value between
 * the dividing line and its neighbours, and will shorten the line at either end by the same amount:
 *
 * ```jsx
 * <Box space={2}><Divider /></Box>
 * ```
 *
 * ## Accessibility
 *
 * For accessibility purposes a divider component will be described with ARIA attributes, i.e. `role="separator"` and `aria-orientation="vertical/horizontal"`.
 */
const Divider = /*#__PURE__*/_react.default.forwardRef((_ref, ref) => {
  let {
    variant,
    vertical = false,
    space,
    tokens,
    ...rest
  } = _ref;
  const {
    color,
    width
  } = (0, _ThemeProvider.useThemeTokens)('Divider', tokens, variant);
  const borderStyles = vertical ? {
    borderRightColor: color,
    borderRightWidth: width
  } : {
    borderBottomColor: color,
    borderBottomWidth: width
  };
  const a11y = _Platform.default.OS === 'web' ?
  // There are aria attributes for dividers on web
  {
    // Early versions of React Native Web support 'aria-orientation' while
    // later versions only support accessibilityOrientation and map it. Supply both.
    'aria-orientation': vertical ? 'vertical' : 'horizontal',
    accessibilityOrientation: vertical ? 'vertical' : 'horizontal',
    accessibilityRole: 'separator'
  } :
  // There are no such equivalent attributes for native
  {};
  const {
    testID,
    ...selectedProps
  } = selectProps({
    ...a11y,
    ...rest
  });
  const divider = /*#__PURE__*/(0, _jsxRuntime.jsx)(_View.default, {
    ref: ref,
    style: [styles.divider, borderStyles],
    testID: testID,
    ...selectedProps
  });
  if (!space) return divider;
  const spacerProps = {
    space,
    direction: vertical ? 'row' : 'column'
  };
  return /*#__PURE__*/(0, _jsxRuntime.jsxs)(_jsxRuntime.Fragment, {
    children: [/*#__PURE__*/(0, _jsxRuntime.jsx)(_Spacer.default, {
      ...spacerProps,
      testID: testID ? `${testID}-Spacer-before` : undefined
    }), divider, /*#__PURE__*/(0, _jsxRuntime.jsx)(_Spacer.default, {
      ...spacerProps,
      testID: testID ? `${testID}-Spacer-after` : undefined
    })]
  });
});
Divider.displayName = 'Divider';
Divider.propTypes = {
  ...selectedSystemPropTypes,
  tokens: (0, _utils.getTokensPropType)('Divider'),
  /**
   * A number referring to a position on the space scale to create space on either side of the divider
   */
  space: _utils.spacingProps.types.spacingValue,
  variant: _utils.variantProp.propType,
  /**
   * By default, the divider is a horizontal line the full width of its parent.
   * With `vertical` prop and when inside a container with { flexDirection: row }, draws a full height vertical line.
   */
  vertical: _propTypes.default.bool
};
const styles = _StyleSheet.default.create({
  divider: {
    alignSelf: 'stretch'
  }
});
var _default = exports.default = Divider;